'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, LayoutDashboard } from 'lucide-react';

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, profile, loading } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => { if (window.innerWidth >= 768) setMobileOpen(false); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 flex-shrink-0">
            <AppLogo size={28} />
            <span className="font-bold text-sm sm:text-base tracking-tight gradient-text">CreatorStudio AI</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-5 lg:gap-6 text-sm">
            {['Features', 'Pricing', 'Creators', 'Blog', 'Docs']?.map((item) => (
              <a
                key={item}
                href={`#${item?.toLowerCase()}`}
                className="text-muted-foreground hover:text-foreground transition-colors font-medium"
              >
                {item}
              </a>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            {!loading && (
              user ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-[11px] font-bold overflow-hidden flex-shrink-0">
                      {profile?.avatar_url ? (
                        <img src={profile?.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        (profile?.username || user?.email || 'U')?.[0]?.toUpperCase()
                      )}
                    </div>
                    <span className="font-medium text-foreground hidden lg:block">{profile?.username || user?.email?.split('@')?.[0]}</span>
                  </div>
                  <Link
                    href="/dashboard"
                    className="flex items-center gap-2 text-sm font-semibold bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-all duration-150 active:scale-95"
                  >
                    <LayoutDashboard size={14} /> Dashboard
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    href="/login"
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/register"
                    className="text-sm font-semibold bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-all duration-150 active:scale-95"
                  >
                    Start Free
                  </Link>
                </>
              )
            )}
          </div>

          {/* Mobile: right side actions */}
          <div className="flex md:hidden items-center gap-1">
            {!loading && user && (
              <Link
                href="/dashboard"
                className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-white px-3 py-2 rounded-lg transition-all active:scale-95 touch-manipulation"
              >
                <LayoutDashboard size={13} /> App
              </Link>
            )}
            <button
              className="p-2.5 rounded-xl hover:bg-muted active:bg-muted transition-colors touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t border-border py-3 space-y-1 animate-fade-in bg-background/98 backdrop-blur-md">
            {['Features', 'Pricing', 'Creators', 'Blog', 'Docs']?.map((item) => (
              <a
                key={item}
                href={`#${item?.toLowerCase()}`}
                className="flex items-center px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition-colors touch-manipulation min-h-[44px]"
                onClick={() => setMobileOpen(false)}
              >
                {item}
              </a>
            ))}
            <div className="pt-2 border-t border-border flex flex-col gap-2 px-1">
              {user ? (
                <Link
                  href="/dashboard"
                  className="px-4 py-3 text-sm font-semibold bg-primary text-white rounded-xl text-center transition-all active:scale-95 touch-manipulation"
                  onClick={() => setMobileOpen(false)}
                >
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    href="/login"
                    className="px-4 py-3 text-sm text-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition-colors touch-manipulation"
                    onClick={() => setMobileOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/register"
                    className="px-4 py-3 text-sm font-semibold bg-primary text-white rounded-xl text-center transition-all active:scale-95 touch-manipulation"
                    onClick={() => setMobileOpen(false)}
                  >
                    Start Recording Free
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}